import { generateTypes } from '@evmos/eip712';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { MsgUpdateRollappInformation } from '../ts-client/tx';
import { MsgUpdateRollappInformation as MsgUpdateRollappInformationProto } from '../proto/tx_pb';
import {
  createGenesisInfoEip712Message,
  createGenesisInfoProtoMessage,
  createMetadataEip712Message,
  createRollappMetadataProtoMessage,
  GENESIS_INFO_TYPES,
  METADATA_TYPES,
} from './shared';

const MSG_UPDATE_ROLLAPP_TYPES = (params: MsgUpdateRollappInformation) => ({
  MsgValue: [
    { name: 'owner', type: 'string' },
    { name: 'rollapp_id', type: 'string' },
    ...(params.initialSequencer ? [ { name: 'initial_sequencer', type: 'string' } ] : []),
    ...(params.minSequencerBond ? [ { name: 'min_sequencer_bond', type: 'TypeMinSequencerBond' } ] : []),
    ...(params.metadata ? [ { name: 'metadata', type: 'TypeMetadata' } ] : []),
    ...(params.genesisInfo ? [ { name: 'genesis_info', type: 'TypeGenesisInfo' } ] : []),
  ],
  ...(params.minSequencerBond && {
    TypeMinSequencerBond: [
      { name: 'denom', type: 'string' },
      { name: 'amount', type: 'string' },
    ],
  }),
  ...METADATA_TYPES(params.metadata),
  ...GENESIS_INFO_TYPES(params.genesisInfo),
});

export const createTxMsgUpdateRollapp = (context: TxContext, params: MsgUpdateRollappInformation): TxPayload => {
  const typedData = createEIP712MsgUpdateRollapp(context, params);
  const cosmosMsg = createCosmosMsgUpdateRollapp(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgUpdateRollapp = (context: TxContext, params: MsgUpdateRollappInformation): EIP712TypedData => {
  const types = generateTypes(MSG_UPDATE_ROLLAPP_TYPES(params));
  const message = createMsgUpdateRollapp(params);
  return { types, message };
};

const createCosmosMsgUpdateRollapp = (context: TxContext, params: MsgUpdateRollappInformation): any => {
  const message = new MsgUpdateRollappInformationProto({
    owner: params.owner,
    rollappId: params.rollappId,
    initialSequencer: params.initialSequencer,
    minSequencerBond: params.minSequencerBond,
    ...createRollappMetadataProtoMessage(params.metadata),
    ...createGenesisInfoProtoMessage(params.genesisInfo),
  });
  return { message, path: MsgUpdateRollappInformationProto.typeName };
};

function createMsgUpdateRollapp(params: MsgUpdateRollappInformation): object | object[] {
  return {
    type: 'rollapp/UpdateRollappInformation',
    value: {
      owner: params.owner,
      rollapp_id: params.rollappId,
      ...(params.initialSequencer && { initial_sequencer: params.initialSequencer }),
      ...(params.minSequencerBond && { min_sequencer_bond: params.minSequencerBond }),
      ...createMetadataEip712Message(params.metadata),
      ...createGenesisInfoEip712Message(params.genesisInfo),
    },
  };
}
